import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, ArrowDownRight, ArrowUpRight, Building2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BankBookTab = ({ getAuthHeader, fromDate, toDate, accounts }) => {
  const [bankRegister, setBankRegister] = useState({ entries: [], summary: {}, bank_accounts: [], pagination: null });
  const [showBankEntry, setShowBankEntry] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  
  const today = new Date().toISOString().split('T')[0];
  const [entryForm, setEntryForm] = useState({
    entry_date: today,
    account_id: '',
    transaction_type: 'receipt',
    contra_account_id: '',
    amount: '',
    narration: '',
    cheque_no: '',
    cheque_date: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const fetchBankRegister = async (page = 1) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/accounting/bank/register?from_date=${fromDate}&to_date=${toDate}&page=${page}&page_size=${pageSize}`,
        { headers: getAuthHeader() }
      );
      setBankRegister(res.data);
      setCurrentPage(page);
    } catch (error) {
      console.error('Failed to fetch bank register');
    }
  };

  useEffect(() => {
    fetchBankRegister(1);
  }, [fromDate, toDate]);

  const handleBankEntry = async () => {
    if (!entryForm.account_id) {
      toast.error('Please select a bank account');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/accounting/bank/entry`, {
        ...entryForm,
        amount: parseFloat(entryForm.amount)
      }, { headers: getAuthHeader() });
      
      toast.success('Bank entry created');
      setShowBankEntry(false);
      fetchBankRegister(1);
      setEntryForm({
        entry_date: today,
        account_id: '',
        transaction_type: 'receipt',
        contra_account_id: '',
        amount: '',
        narration: '',
        cheque_no: '',
        cheque_date: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create entry');
    }
  };

  const pagination = bankRegister.pagination;

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Bank Book</CardTitle>
            <CardDescription>Bank receipts and payments</CardDescription>
          </div>
          <Button onClick={() => setShowBankEntry(true)} data-testid="new-bank-entry-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Entry
          </Button>
        </CardHeader>
        <CardContent>
          {/* Bank Accounts Summary */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {bankRegister.bank_accounts?.map((bank) => (
              <Card key={bank.id}>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-blue-600" />
                    <p className="text-sm text-muted-foreground">{bank.account_name}</p>
                  </div>
                  <p className="text-xl font-bold">{formatCurrency(bank.current_balance)}</p>
                  {bank.bank_name && (
                    <p className="text-xs text-muted-foreground">{bank.bank_name}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Voucher No</TableHead>
                <TableHead>Narration</TableHead>
                <TableHead>Cheque No</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bankRegister.entries?.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{entry.entry_date}</TableCell>
                  <TableCell className="font-mono">{entry.voucher_no}</TableCell>
                  <TableCell>{entry.narration}</TableCell>
                  <TableCell>{entry.cheque_no || '-'}</TableCell>
                  <TableCell>
                    <Badge variant={entry.transaction_type === 'receipt' ? 'default' : 'destructive'}>
                      {entry.transaction_type}
                    </Badge>
                  </TableCell>
                  <TableCell className={`text-right font-medium ${
                    entry.transaction_type === 'receipt' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatCurrency(entry.amount)}
                  </TableCell>
                </TableRow>
              ))}
              {bankRegister.entries?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No bank entries found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pagination && pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, pagination.total)} of {pagination.total} entries
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchBankRegister(currentPage - 1)}
                  disabled={!pagination.has_prev}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <span className="text-sm px-2">
                  Page {currentPage} of {pagination.total_pages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fetchBankRegister(currentPage + 1)}
                  disabled={!pagination.has_next}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bank Entry Dialog */}
      <Dialog open={showBankEntry} onOpenChange={setShowBankEntry}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Bank Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={entryForm.entry_date}
                  onChange={(e) => setEntryForm({ ...entryForm, entry_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Type</Label>
                <Select
                  value={entryForm.transaction_type}
                  onValueChange={(v) => setEntryForm({ ...entryForm, transaction_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="receipt">Receipt</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Bank Account</Label>
              <Select
                value={entryForm.account_id}
                onValueChange={(v) => setEntryForm({ ...entryForm, account_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select bank account..." />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter(a => a.is_bank_account).map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.account_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Account (Dr/Cr)</Label>
              <Select
                value={entryForm.contra_account_id}
                onValueChange={(v) => setEntryForm({ ...entryForm, contra_account_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select account..." />
                </SelectTrigger>
                <SelectContent>
                  {accounts.filter(a => !a.is_bank_account).map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.account_code} - {acc.account_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Amount</Label>
              <Input
                type="number"
                value={entryForm.amount}
                onChange={(e) => setEntryForm({ ...entryForm, amount: e.target.value })}
                placeholder="Enter amount"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cheque No (Optional)</Label>
                <Input
                  value={entryForm.cheque_no}
                  onChange={(e) => setEntryForm({ ...entryForm, cheque_no: e.target.value })}
                />
              </div>
              <div>
                <Label>Cheque Date</Label>
                <Input
                  type="date"
                  value={entryForm.cheque_date}
                  onChange={(e) => setEntryForm({ ...entryForm, cheque_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Narration</Label>
              <Input
                value={entryForm.narration}
                onChange={(e) => setEntryForm({ ...entryForm, narration: e.target.value })}
                placeholder="Description of transaction"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBankEntry(false)}>Cancel</Button>
            <Button onClick={handleBankEntry} data-testid="save-bank-entry-btn">Save Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BankBookTab;
